/** Reading a stored document - the act behind "Read this file" - as one hook (moved out of the old
 * Evidence screen, ADR-0212).
 *
 * `POST /evidence/{id}/extract` takes no body: the route reads the stored object (ADR-0028), so the
 * digest on the document covers the text its figures came from. The answer is kept, not discarded
 * (EXP-35): `already_extracted` says this request read nothing because an earlier one had, and
 * `redacted_items` is `null` on that path - *nobody asked*, as against *asked and found nothing*.
 * `ReadOutcome` renders the three answers so that they can never look alike.
 *
 * **Only a `validated` document is readable.** A spreadsheet is imported, not read, and a document
 * in a state nothing moves on has no button (`resume.WAITING_FOR` answers `re_upload`); aiming this at
 * either is a control that predictably fails (UX-04), so callers pass this only where it can run.
 */

import { useState } from "react";

import { useExtract } from "../../api/evidence";
import type { Extracted } from "../../api/evidence";

export function useReadDocument() {
  const extract = useExtract();
  const [target, setTarget] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Extracted | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);

  async function read(documentId: string) {
    setRefusal(null);
    setOutcome(null);
    setTarget(documentId);
    try {
      setOutcome(await extract.mutateAsync(documentId));
    } catch (error) {
      // The remedy travels with the refusal (EXP-05): a scan, a damaged file and a kind EmitIQ does
      // not read are three different next steps, and `WriteFailed` reads the status and the code.
      setRefusal(error);
    } finally {
      setTarget(null);
    }
  }

  return {
    read,
    pending: extract.isPending,
    /** The document being read right now, so only its button says "Reading". */
    target,
    outcome,
    refusal,
    clearRefusal: () => setRefusal(null),
  };
}
